/**
 * FRONT-2486: what a person reads for a notification — one pure function
 * both the bell popover and the Notifications page render through.
 *
 * The rows are written server-side (`src/autonomy/gate.ts`'s `informAfter`
 * and `noticeApprovedButRefused`), and the text they store is built for a
 * log, not for a person: "EDDY ran work_task_create", a body led by
 * "work_task_create (title=…)", and a settings path printed in parentheses.
 * Cleaning it here, at display time, rather than in the writer has two
 * reasons: the only per-tool display-name table is the frontend's
 * `humanizeToolName` (a second copy on the server would drift), and the rows
 * already stored — hundreds of them on a busy account — heal without a
 * migration.
 *
 * Relative imports only: the Notifications browser specs import this file
 * to know what a row will read as (they cannot resolve the `~` alias).
 *
 * The tool name is read from the row's dedupe key
 * (`autonomy_inform:<tool>:<bucket>`), never guessed out of the prose, so a
 * word that merely looks like a tool name is never rewritten.
 */
import { humanizeToolName } from './humanize-tool-name';
import { classifyNotificationHref } from './notification-href';

interface NotificationTextInput {
  kind: string;
  title: string;
  body: string | null;
  dedupeKey: string | null;
}

const TOOL_NOTICE_KINDS = new Set(['autonomy_inform', 'autonomy_refused']);

/** The tool a tier-2/3 notice is about, from its dedupe key; null otherwise. */
export function notificationToolName(n: Pick<NotificationTextInput, 'kind' | 'dedupeKey'>): string | null {
  if (!TOOL_NOTICE_KINDS.has(n.kind) || !n.dedupeKey) return null;
  const parts = n.dedupeKey.split(':');
  if (parts[0] !== n.kind || parts.length < 3) return null;
  const tool = parts[1];
  return /^[A-Za-z0-9_.-]+$/.test(tool) ? tool : null;
}

/** `title=Foo · cmd=bar` → `Foo · bar`: the values stay, the argument names go. */
function stripArgNames(tags: string): string {
  return tags
    .split(' · ')
    .map((part) => part.replace(/^[a-z_]+=/, ''))
    .join(' · ');
}

/**
 * Wiz review (PR #1780): no `new RegExp` over data here, and no pattern that
 * rescans a line. The tool name and the body are notification data, so both
 * rewrites are plain string work — each line is looked at once. The only
 * regexes left in this file are literals over input of bounded length.
 */
function isLead(ch: string | undefined): boolean {
  return ch === undefined || ch === ':' || ch === ' ' || ch === '\t';
}

/** One line of `humaniseToolCall` output: `<tool> (k=v · k=v)` or a bare `<tool>`, at a word start. */
function humaniseLine(line: string, tool: string, human: string): string {
  const open = `${tool} (`;
  if (line.endsWith(')')) {
    for (let at = line.indexOf(open); at !== -1; at = line.indexOf(open, at + 1)) {
      if (!isLead(line[at - 1])) continue;
      return `${line.slice(0, at)}${human}: ${stripArgNames(line.slice(at + open.length, -1))}`;
    }
  }
  if (line.endsWith(tool) && isLead(line[line.length - tool.length - 1])) {
    return `${line.slice(0, line.length - tool.length)}${human}`;
  }
  return line;
}

export function displayNotification(n: NotificationTextInput): { title: string; body: string | null } {
  const tool = notificationToolName(n);
  if (!tool) return { title: n.title, body: n.body };
  const human = humanizeToolName(tool);

  // "EDDY ran work_task_create" / the older "Eddy ran …" spelling.
  let title = n.title;
  for (const who of ['EDDY', 'Eddy']) {
    if (n.title === `${who} ran ${tool}`) title = `${who}: ${human}`;
  }

  let body = n.body;
  if (body) {
    body = body
      .split('\n')
      .map((line) => humaniseLine(line, tool, human))
      .join('\n');
    // When the title already names the tool, the first paragraph need not
    // repeat it: drop a bare repeat, and keep only what was acted on.
    if (title !== n.title) {
      const [first, ...rest] = body.split('\n\n');
      if (first.trim() === human && rest.length > 0) body = rest.join('\n\n');
      else if (first.startsWith(`${human}: `)) body = [first.slice(human.length + 2), ...rest].join('\n\n');
    }
  }
  return { title, body };
}

export type NotificationTextPart = string | { label: string; href: string };

/**
 * Split a body into text and links: "System Settings → Policy
 * (/system-settings?section=policy)" and the older "the Policy page
 * (/policy)" become a link on the words, with the parenthesised path gone.
 * Only an in-app path the notification link classifier calls `internal` is
 * linked — a protocol-relative `//host` in an admin-written broadcast stays
 * plain text.
 */
const LABEL_WINDOW = 80;
/** Matched against at most LABEL_WINDOW characters, never the whole body. */
const BREADCRUMB_LABEL = /(?:^|(?<= ))[A-Z][\w-]*(?: [A-Z][\w-]*)*(?: → [A-Z][\w-]*(?: [A-Z][\w-]*)*)+$/;
const PAGE_LABEL = /(?:^|(?<= ))the [A-Z][\w-]* page$/;

export function splitNotificationLinks(text: string): NotificationTextPart[] {
  const parts: NotificationTextPart[] = [];
  let last = 0;
  for (let open = text.indexOf(' (/'); open !== -1; open = text.indexOf(' (/', open + 1)) {
    // The path runs to the first ')' and may hold no whitespace, so the scan
    // stops at the first space: it never crosses into the next ' (/', and the
    // body is walked once overall.
    let close = open + 3;
    while (close < text.length && text[close] !== ')' && !/\s/.test(text[close])) close++;
    if (text[close] !== ')') continue;
    const href = text.slice(open + 2, close);
    if (classifyNotificationHref(href) !== 'internal') continue;
    const before = text.slice(Math.max(last, open - LABEL_WINDOW), open);
    const label = (BREADCRUMB_LABEL.exec(before) ?? PAGE_LABEL.exec(before))?.[0];
    if (!label) continue;
    const at = open - label.length;
    if (at > last) parts.push(text.slice(last, at));
    parts.push({ label, href });
    last = close + 1;
    open = close;
  }
  if (last < text.length) parts.push(text.slice(last));
  return parts;
}
